/**
 * Read-only card for the profile fields shared by specialists and consultants:
 * contact preferences, coverage areas, clinic and usual weekly working hours.
 */
import { Box, Card, CardContent, Chip, Divider, Grid, Link, Typography } from '@mui/material';
import {
  ContactPhone as ContactIcon,
  Place as PlaceIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ProviderProfile } from '../../types/providerProfile';
import { WEEK_DAYS, providerProfileFromApi } from '../../utils/providerProfile';

const boxSx = {
  p: 2,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.paper',
  height: '100%',
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={boxSx}>
    <Typography variant="caption" color="text.secondary" fontWeight={500} display="block" sx={{ mb: 1 }}>
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={500} component="div" sx={{ whiteSpace: 'pre-line' }}>
      {children}
    </Typography>
  </Box>
);

const SectionTitle = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
    {icon}
    <Typography variant="h6" fontWeight={600}>
      {children}
    </Typography>
  </Box>
);

const ProviderProfileDetails = ({ provider }: { provider: Partial<ProviderProfile> }) => {
  const { t } = useTranslation();
  const profile = providerProfileFromApi(provider);
  const none = '—';

  const chips = (values: string[]) =>
    values.length ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {values.map((value) => (
          <Chip key={value} label={value} size="small" />
        ))}
      </Box>
    ) : (
      none
    );

  const workingDays = WEEK_DAYS.map((day) => ({
    day,
    hours: profile.weekly_schedule.filter((entry) => entry.day === day),
  }));

  return (
    <Card>
      <CardContent>
        {/* Contact preferences */}
        <SectionTitle icon={<ContactIcon sx={{ color: 'primary.main' }} />}>
          {t('providers.sections.contact')}
        </SectionTitle>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Field label={t('providers.form.contactMethod')}>
              {profile.preferred_contact_method
                ? t(`users.contactMethods.${profile.preferred_contact_method}`)
                : none}
            </Field>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Field label={t('providers.form.contactTime')}>{profile.preferred_contact_time || none}</Field>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Field label={t('providers.form.contactNotes')}>{profile.contact_notes || none}</Field>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Coverage & clinic */}
        <SectionTitle icon={<PlaceIcon sx={{ color: 'primary.main' }} />}>
          {t('providers.sections.coverage')}
        </SectionTitle>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Field label={t('providers.form.cities')}>{chips(profile.service_cities)}</Field>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Field label={t('providers.form.areas')}>{chips(profile.service_areas)}</Field>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Field label={t('providers.form.clinicAddress')}>
              {profile.clinic_address || none}
              {profile.clinic_location_url && (
                <Box sx={{ mt: profile.clinic_address ? 1 : 0 }}>
                  <Link href={profile.clinic_location_url} target="_blank" rel="noopener noreferrer">
                    {t('providers.detail.openMap')}
                  </Link>
                </Box>
              )}
            </Field>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Usual working hours */}
        <SectionTitle icon={<ScheduleIcon sx={{ color: 'primary.main' }} />}>
          {t('providers.sections.schedule')}
        </SectionTitle>
        {profile.weekly_schedule.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('providers.detail.noSchedule')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {workingDays.map(({ day, hours }) => (
              <Box
                key={day}
                sx={{
                  ...boxSx,
                  height: 'auto',
                  minWidth: 130,
                  py: 1.5,
                  opacity: hours.length ? 1 : 0.5,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {t(`providers.days.${day}`)}
                </Typography>
                {hours.length ? (
                  hours.map((entry) => (
                    <Typography key={`${entry.from}-${entry.to}`} variant="body2" color="text.secondary" dir="ltr">
                      {entry.from} – {entry.to}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('providers.detail.dayOff')}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ProviderProfileDetails;
